import 'assets/styles/compliance.scss';

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useStore } from 'hooks';
import * as config from 'config/constants.js';
import Grid from '@material-ui/core/Grid';
import DateFnsUtils from '@date-io/date-fns';
import { Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import AnnouncementOutlinedIcon from "@material-ui/icons/AnnouncementOutlined";
import useDashboardStyles from "../../assets/styles/useDashboardStyles";
import {format} from "date-fns";

const useStyles = makeStyles(() => ({
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start', // ✅ anchor top
    flexWrap: 'nowrap',
    gap: '1rem',
    padding: '0.5rem 1rem 0 1rem', // ✅ reduce top padding
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    minWidth: 0,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  headerIcon: {
    fontSize: 'clamp(1rem, 2vw, 1.5rem)',
    color: '#e0123c',
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: 'clamp(0.6rem, 1.5vw, 1.2rem)', // smaller clamp range
    margin: 0,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 1,   // ✅ critical: allow it to shrink
    minWidth: 0,     // ✅ critical: allow overflow handling inside flex
  },
  rightSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    paddingTop: 0,
    marginTop: 0,
  },
  datePicker: {
    flexShrink: 1,
    minWidth: 0,
    maxWidth: '140px',
    margin: 0, // ✅ remove bottom spacing
    padding: 0,
    '& input': {
      fontSize: 'clamp(0.6rem, 1.2vw, 0.9rem)',
    },
    '& .MuiInputBase-root': {
      fontSize: 'clamp(0.6rem, 1.2vw, 0.9rem)',
      marginTop: 0,
    },
    '& .MuiInputLabel-root': {
      fontSize: 'clamp(0.6rem, 1.2vw, 0.9rem)',
      transform: 'translate(14px, 12px) scale(1)',
    },
  },
  searchButton: {
    fontSize: 'clamp(0.6rem, 1.2vw, 0.9rem)',
    padding: '0.25rem 0.75rem',
    minWidth: '80px',
    alignSelf: 'flex-start', // ✅ align to top
    marginTop: 0,
  },
}));

const MicroRegulatoryProcessesList = ({ embedded}) => {
  const classes = useStyles();
  const classesDash = useDashboardStyles();
  const [microRegulatoryProcessNext, setMicroRegulatoryProcessNext] = useState('');
  const [microRegulatoryProcessData, setMicroRegulatoryProcessData] = useState();
  const [microRegulatoryProcessCount, setMicroRegulatoryProcessCount] = useState(0);
  const [microRegulatoryProcessDisplayCount, setMicroRegulatoryProcessDisplayCount] = useState(0);
  const [microRegulatoryProcessLoading, setMicroRegulatoryProcessLoading] = useState(false);
  const store = useStore();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const numRecordPage = config.pageSize;
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [selectedDateEnd, setSelectedDateEnd] = React.useState(new Date());


  const getMicroRegulatoryProcessList = async () => {
    try {
      const { data } = await store.api.getMicroProcessesReviewed();
      setMicroRegulatoryProcessData(data.results);
      setMicroRegulatoryProcessNext(data.next);
      setMicroRegulatoryProcessCount(data.count);
      data.count > numRecordPage
        ? setMicroRegulatoryProcessDisplayCount(numRecordPage)
        : setMicroRegulatoryProcessDisplayCount(data.count);
    } catch (error) {
      if (error.response && error?.response?.status === 403) {
        enqueueSnackbar('You do not have permission to view this data', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred, please contact support', {
          variant: 'error',
        });
      }
    }
  };
  const formatDate = (date) => {
    const d = new Date(date);
    let day = '' + d.getDate(),
        month = '' + (d.getMonth() + 1), // Months are zero-based
        year = d.getFullYear();

    if (month.length < 2)
        month = '0' + month;
    if (day.length < 2)
        day = '0' + day;

    return [day, month, year].join('-');
}


  const getMicroRegulatoryProcessReviewedBetween = async () => {
    try {
      const startDateString = formatDate(selectedDate);
      const endDateString = formatDate(selectedDateEnd);
      const { data } = await store.api.getMicroProcessesReviewedBetween(startDateString, endDateString);
      setMicroRegulatoryProcessData(data.results);
      setMicroRegulatoryProcessNext(data.next);
      setMicroRegulatoryProcessCount(data.count);
      data.count > numRecordPage
        ? setMicroRegulatoryProcessDisplayCount(numRecordPage)
        : setMicroRegulatoryProcessDisplayCount(data.count);
    } catch (error) {
      if (error.response && error?.response?.status === 403) {
        enqueueSnackbar('You do not have permission to view this data', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred, please contact support', {
          variant: 'error',
        });
      }
    }
  };

  const getNextMicroRegulatoryProcesses = async () => {
    try {
      setMicroRegulatoryProcessLoading(true);
      const { data } = await store.api.getNextDataWithURL(microRegulatoryProcessNext);
      let newMicroRegulatoryProcesses = microRegulatoryProcessData;
      setMicroRegulatoryProcessNext(data.next);
      setMicroRegulatoryProcessData(undefined);
      const arrayLength = data.results.length;
      data.results.map((process, i) => {
        newMicroRegulatoryProcesses.push(process);
        if (arrayLength == i + 1) {
          setMicroRegulatoryProcessData(newMicroRegulatoryProcesses);
          setMicroRegulatoryProcessDisplayCount(microRegulatoryProcessDisplayCount + arrayLength);
        }
      });
    } catch (error) {
      setMicroRegulatoryProcessLoading(false);
      console.log(error);
      if (error.response && error?.response?.status === 403) {
        enqueueSnackbar('You do not have permission to view this data', {
          variant: 'error',
        });
      } else {
        enqueueSnackbar('An error occurred, please contact support', {
          variant: 'error',
        });
      }
    } finally {
      setMicroRegulatoryProcessLoading(false);
    }
  };

  useEffect(() => {
    getMicroRegulatoryProcessList();
  }, []);

  const navigateToMicroRegulatoryProcess = (microProcessId) => {
    const path = 'user-micro-regulatory-process-detail?micro_process='+microProcessId;
    history.push(path);
  };

  const handleDateChange = (date) => {
      setSelectedDate(date);
    };
  const handleDateChangeEnd = (date) => {
    setSelectedDateEnd(date);
  };
  const handleSearch = () => {
    // Your search logic here
    getMicroRegulatoryProcessReviewedBetween();
    console.log('Search button clicked!');
};

  return (
    <section className={'compliance-container'}  style={
          embedded
            ? { width: '100%', position: 'relative', left: '10px' }
            : undefined
        }>
       {!embedded && ( <div className="back-nav" onClick={() => history.push('/companies-dashboard')}>
        Home
      </div> )}

    <div className={classes.topBar}>
      <div className={classes.leftSection}>
        <AnnouncementOutlinedIcon className={classes.headerIcon} />
        <h4 className={classes.headerTitle}>
          Compliance Monitoring Programme Dashboard
        </h4>
      </div>

      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <div className={classes.rightSection}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            id="start-date"
            value={selectedDate}
            onChange={handleDateChange}
            className={classes.datePicker}
            KeyboardButtonProps={{ 'aria-label': 'change date' }}
          />
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="dd/MM/yyyy"
            margin="normal"
            id="end-date"
            value={selectedDateEnd}
            onChange={handleDateChangeEnd}
            className={classes.datePicker}
            KeyboardButtonProps={{ 'aria-label': 'change date' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            className={classes.searchButton}
          >
            Search
          </Button>
        </div>
      </MuiPickersUtilsProvider>
    </div>

      <ul className="categories-list">
          <li key="count" style={{ backgroundColor: '#f4f5f7' }}>
         {!embedded && ( <div>
          <div>
          <div>This screen lists all the Regulatory Micro-processes that this user is the owner of that need to be actioned in the next month or those that have failed validation by the Automated Compliance Testing Module.&nbsp;</div>
          <div>&nbsp;</div>
          <div><strong>ACTION</strong>:&nbsp; Process Owners will be responsible for either Fixing Issues and/or Reviewing and Signing off the Risk Assessment, Policy, Procedures documents, uploading the applicable Evidence Files/Reports and completing the Compliance Monitoring Programme.&nbsp;</div>
          <div><strong>RESULT</strong>: Regulatory Micro-processes will be removed from this screen when they have passed validation and appear again 1 month from the Next Review Date as part of the Compliance Monitoring Programme.</div>
          </div>
               <div>&nbsp;</div>
          </div>

           )}

           <div className={classesDash.statusTextWrapper}>
            <span className={classesDash.statusText}>
              Displaying {microRegulatoryProcessDisplayCount} of {microRegulatoryProcessCount} Micro Regulatory Processes
            </span>
          </div>
        </li>
        {microRegulatoryProcessData !== undefined &&
          microRegulatoryProcessData.map((microProcess, i) => {
            const microProcessId = microProcess.id;

            return (
                <li
                  key={i}
                  className={classesDash.listItem}
                  onClick={() => navigateToMicroRegulatoryProcess(microProcessId)}
                >
                  <span className={classesDash.field}>{microProcessId}</span>
                  <span className={`${classesDash.field} ${classesDash.name}`}>
                    {microProcess.micro_regulatory_process.name}
                  </span>
                  <span className={`${classesDash.field} ${classesDash.owner}`}>
                    {microProcess.owner.first_name} {microProcess.owner.last_name}
                  </span>
                  <span className={`${classesDash.field} ${classesDash.date}`}>
                      {format(new Date(microProcess.last_updated), 'yyyy-MM-dd HH:mm:ss')}

                  </span>
                  {!embedded && (<span
                    className={`${classesDash.status} ${
                      microProcess.validation_status === 'Passed' ? 'status green-status' : 'status red-status'
                    }`}
                  />)}
                  {embedded && (
                   <span
                      className={`${classesDash.status} ${
                        microProcess.validation_status === 'Passed' ? classesDash.greenStatus : classesDash.redStatus
                      }`}
                    />
                    )
                  }
                </li>
            );
          })}
        {microRegulatoryProcessCount > numRecordPage && (
          <li className="get-next" key="next" onClick={() => getNextMicroRegulatoryProcesses()}>
            <span>{microRegulatoryProcessLoading ? 'Loading...' : 'Load ' + numRecordPage.toString() + ' more'}</span>
          </li>
        )}
      </ul>
    </section>
  );
};

MicroRegulatoryProcessesList.defaultProps = {};

export default MicroRegulatoryProcessesList;